import { useSubmissionVisibility } from "@/hooks/useSubmissionVisibility";
import { ReviewMode, usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import { appstr } from "@coreapp/utils/appstr";
import { Button, Flex, ListItemOption, Menu } from "@theorgchartdev/storybook";
import { useMemo } from "react";

const PlanSubmissionButtons = () => {
  const { planManager } = usePlanManagerContext();

  const planSubmission = planManager?.state?.GetPlanSubmission();

  const { isReviewer, hasActiveSubmission, getCurrentUserReviewStatus } =
    useSubmissionVisibility(planSubmission);
  const { canReviewPlans, canSubmitPlans } = getPlanPermissions({ useCurrentPlan: true });

  const enablePlanApprovals = planManager?.state?.GetApprovalSettings()?.enablePlanApprovals;

  if (!enablePlanApprovals) {
    return null;
  }

  return (
    <Flex gap="16px">
      {canSubmitPlans && !hasActiveSubmission && <SubmitButton />}
      {canSubmitPlans && hasActiveSubmission && <WithdrawButton />}
      {canReviewPlans && isReviewer && hasActiveSubmission && (
        <ReviewMenu reviewerStatus={getCurrentUserReviewStatus() as ReviewerStatus} />
      )}
    </Flex>
  );
};

export default PlanSubmissionButtons;

interface ReviewMenuProps {
  reviewerStatus: ReviewerStatus;
}

const ReviewMenu = ({ reviewerStatus }: ReviewMenuProps) => {
  const {
    planReviewContextValue: { openReviewModal }
  } = usePlanManagerContext();

  const canApprove = reviewerStatus !== ReviewerStatus.Approved;
  const canReject = reviewerStatus !== ReviewerStatus.Rejected;

  const handleApprove = () => {
    openReviewModal(ReviewMode.APPROVE);
  };

  const handleReject = () => {
    openReviewModal(ReviewMode.REJECT);
  };

  const triggerButton = (
    <Button
      modifier="quaternary"
      color="brand"
      iconAlignEnd
      iconProps={{ name: "chevron-down", size: "xsmall" }}
      dataTestId="review-plan-trigger"
    >
      {appstr("Review plan")}
    </Button>
  );

  const menuOptions: ListItemOption[] = useMemo(
    () => [
      {
        label: appstr("Approve plan"),
        value: "approve",
        icon: "circle-check",
        disabled: !canApprove,
        tooltip: !canApprove ? appstr("You have already approved this plan") : null,
        clickHandler: handleApprove,
        dataTestId: "review-plan-approve-option"
      },
      {
        label: appstr("Reject plan"),
        value: "reject",
        icon: "circle-xmark",
        disabled: !canReject,
        tooltip: !canReject ? appstr("You have already rejected this plan") : null,
        clickHandler: handleReject,
        dataTestId: "review-plan-reject-option"
      }
    ],
    [canApprove, canReject, handleApprove, handleReject]
  );

  return <Menu trigger={triggerButton} options={menuOptions} />;
};

const SubmitButton = () => {
  const {
    planSubmitContextValue: { openSubmitModal }
  } = usePlanManagerContext();

  return (
    <Button
      modifier="quaternary"
      color="brand"
      iconProps={{
        name: "inbox-out"
      }}
      onClick={openSubmitModal}
      dataTestId="submit-plan-header-button"
    >
      {appstr("Submit plan")}
    </Button>
  );
};

const WithdrawButton = () => {
  const {
    planWithdrawContextValue: { openWithdrawModal }
  } = usePlanManagerContext();

  return (
    <Button
      modifier="quaternary"
      color="brand"
      iconProps={{
        name: "inbox-in"
      }}
      onClick={openWithdrawModal}
      dataTestId="withdraw-plan-header-button"
    >
      {appstr("Withdraw plan")}
    </Button>
  );
};
